import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { createLocationGrid } from "@/lib/admin.functions";
import {
  listTenantsOptions, createTenant, createFacility, createCarrier, createCustomer,
} from "@/lib/admin-config.functions";
import { createSku } from "@/lib/replenishment.functions";
import { runServerFn } from "@/lib/server-fn";
import { useAuth, tenantIdsWithRole } from "@/hooks/use-auth";
import { useScope } from "@/hooks/use-scope";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Check, ChevronRight, Loader2, Warehouse, Grid3x3, Truck, Users, Package } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/admin/onboarding")({ component: OnboardingPage });

type Step = "tenant" | "facility" | "grid" | "carrier" | "customer" | "sku" | "done";

const STEPS: { id: Step; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: "tenant", label: "Tenant", icon: Warehouse },
  { id: "facility", label: "Facility", icon: Warehouse },
  { id: "grid", label: "Locations", icon: Grid3x3 },
  { id: "carrier", label: "Carrier", icon: Truck },
  { id: "customer", label: "Customer", icon: Users },
  { id: "sku", label: "First SKU", icon: Package },
];

function OnboardingPage() {
  const qc = useQueryClient();
  const { roles } = useAuth();
  const { isSuperAdmin: su } = useScope();
  const adminTenantIds = tenantIdsWithRole(roles, "tenant_admin");
  const listTenantsFn = useServerFn(listTenantsOptions);

  const [step, setStep] = useState<Step>("tenant");
  const [busy, setBusy] = useState(false);

  // State accumulated across steps
  const [tenantId, setTenantId] = useState("");
  const [facilityId, setFacilityId] = useState("");
  const [customerId, setCustomerId] = useState("");

  const { data: tenants = [] } = useQuery({
    queryKey: ["onboarding-tenants"],
    queryFn: () => listTenantsFn(),
  });
  const allowedTenants = useMemo(
    () => (su ? tenants : tenants.filter((t) => adminTenantIds.includes(t.id))),
    [tenants, su, adminTenantIds]
  );

  const advance = (next: Step) => setStep(next);

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Tenant Onboarding</h2>
        <p className="text-sm text-muted-foreground">
          Stand up a new tenant end-to-end: facility, location grid, carrier, customer, and a first SKU.
        </p>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {STEPS.map((s, i) => {
          const idx = STEPS.findIndex((x) => x.id === step);
          const done = i < idx || step === "done";
          const active = s.id === step;
          const Icon = s.icon;
          return (
            <div key={s.id} className="flex items-center gap-2 shrink-0">
              <div
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs ${
                  active ? "border-primary bg-primary/10 text-foreground"
                    : done ? "border-success/40 bg-success/10 text-success"
                    : "border-border text-muted-foreground"
                }`}
              >
                {done ? <Check className="h-3 w-3" /> : <Icon className="h-3 w-3" />}
                {s.label}
              </div>
              {i < STEPS.length - 1 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
            </div>
          );
        })}
      </div>

      {step === "tenant" && (
        <TenantStep
          su={su} allowedTenants={allowedTenants} busy={busy} setBusy={setBusy}
          onPick={(id) => { setTenantId(id); advance("facility"); }}
          onCreated={async (id) => {
            await qc.invalidateQueries({ queryKey: ["onboarding-tenants"] });
            setTenantId(id); advance("facility");
          }}
        />
      )}
      {step === "facility" && (
        <FacilityStep
          tenantId={tenantId} busy={busy} setBusy={setBusy}
          onCreated={(id) => { setFacilityId(id); advance("grid"); }}
        />
      )}
      {step === "grid" && (
        <GridStep
          facilityId={facilityId} busy={busy} setBusy={setBusy}
          onDone={() => advance("carrier")}
        />
      )}
      {step === "carrier" && (
        <CarrierStep
          tenantId={tenantId} busy={busy} setBusy={setBusy}
          onDone={() => advance("customer")}
        />
      )}
      {step === "customer" && (
        <CustomerStep
          tenantId={tenantId} busy={busy} setBusy={setBusy}
          onCreated={(id) => { setCustomerId(id); advance("sku"); }}
        />
      )}
      {step === "sku" && (
        <SkuStep
          tenantId={tenantId} customerId={customerId} busy={busy} setBusy={setBusy}
          onDone={() => advance("done")}
        />
      )}
      {step === "done" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Check className="h-5 w-5 text-success" /> Tenant ready
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p>Your tenant is operational. Recommended next steps:</p>
            <ul className="list-disc pl-5 text-muted-foreground space-y-1">
              <li>Invite operators under <strong>Users</strong>.</li>
              <li>Configure billing rules and consumables.</li>
              <li>Add putaway and replenishment rules.</li>
              <li>Create your first ASN and inbound shipment.</li>
            </ul>
            <Button variant="outline" onClick={() => { setStep("tenant"); setTenantId(""); setFacilityId(""); setCustomerId(""); }}>
              Onboard another tenant
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ───── Steps ─────

function StepCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent className="space-y-3">{children}</CardContent>
    </Card>
  );
}

function TenantStep({
  su, allowedTenants, busy, setBusy, onPick, onCreated,
}: {
  su: boolean;
  allowedTenants: { id: string; name: string }[];
  busy: boolean;
  setBusy: (v: boolean) => void;
  onPick: (id: string) => void;
  onCreated: (id: string) => void;
}) {
  const [name, setName] = useState("");
  const [pickId, setPickId] = useState("");
  const createTenantFn = useServerFn(createTenant);

  const create = async () => {
    if (!name.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      const res = await createTenantFn({ data: { name: name.trim() } });
      toast.success("Tenant created");
      onCreated(res.id);
    } catch (err) { toast.error((err as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <StepCard title="Choose or Create a Tenant">
      {allowedTenants.length > 0 && (
        <div className="space-y-2">
          <Label>Use existing tenant</Label>
          <div className="flex gap-2">
            <Select value={pickId} onValueChange={setPickId}>
              <SelectTrigger className="flex-1"><SelectValue placeholder="Select tenant" /></SelectTrigger>
              <SelectContent>
                {allowedTenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button disabled={!pickId} onClick={() => onPick(pickId)}>Continue</Button>
          </div>
        </div>
      )}
      {su && (
        <div className="border-t pt-3 space-y-2">
          <Label>Or create a new tenant</Label>
          <div className="flex gap-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme 3PL" />
            <Button onClick={create} disabled={busy}>
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create
            </Button>
          </div>
        </div>
      )}
    </StepCard>
  );
}

function FacilityStep({
  tenantId, busy, setBusy, onCreated,
}: { tenantId: string; busy: boolean; setBusy: (v: boolean) => void; onCreated: (id: string) => void }) {
  const [name, setName] = useState("Main DC");
  const [tz, setTz] = useState("UTC");
  const createFacilityFn = useServerFn(createFacility);

  const create = async () => {
    if (!name.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      const res = await createFacilityFn({ data: { tenantId, name: name.trim(), timezone: tz } });
      toast.success("Facility created");
      onCreated(res.id);
    } catch (err) { toast.error((err as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <StepCard title="Create the First Facility">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label>Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label>Timezone</Label>
          <Input value={tz} onChange={(e) => setTz(e.target.value)} />
        </div>
      </div>
      <Button onClick={create} disabled={busy} className="w-full">
        {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create facility
      </Button>
    </StepCard>
  );
}

function GridStep({
  facilityId, busy, setBusy, onDone,
}: { facilityId: string; busy: boolean; setBusy: (v: boolean) => void; onDone: () => void }) {
  const [zone, setZone] = useState("A");
  const [aisles, setAisles] = useState(4);
  const [bays, setBays] = useState(8);
  const [levels, setLevels] = useState(4);
  const total = aisles * bays * levels;
  const createLocationGridFn = useServerFn(createLocationGrid);

  const generate = async () => {
    if (total > 5000) return toast.error("Max 5000 cells per call");
    setBusy(true);
    try {
      const res = await runServerFn(
        () => createLocationGridFn({ data: { facilityId, zone, aisles, bays, levels } }),
        "Generate grid",
      );
      toast.success(`Created ${res.count} location(s)`);
      onDone();
    } catch {
      // toast handled
    } finally {
      setBusy(false);
    }
  };

  return (
    <StepCard title="Generate a Storage Location Grid">
      <p className="text-xs text-muted-foreground">Codes use the format <code className="font-mono">ZONE-AISLE-BAY-LEVEL</code> (e.g. <code className="font-mono">A-01-03-02</code>).</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <div className="space-y-1"><Label>Zone</Label><Input value={zone} onChange={(e) => setZone(e.target.value)} /></div>
        <div className="space-y-1"><Label>Aisles</Label><Input type="number" min={1} value={aisles} onChange={(e) => setAisles(+e.target.value || 1)} /></div>
        <div className="space-y-1"><Label>Bays</Label><Input type="number" min={1} value={bays} onChange={(e) => setBays(+e.target.value || 1)} /></div>
        <div className="space-y-1"><Label>Levels</Label><Input type="number" min={1} value={levels} onChange={(e) => setLevels(+e.target.value || 1)} /></div>
      </div>
      <Badge variant="secondary">Total: {total} location(s)</Badge>
      <div className="flex gap-2">
        <Button onClick={generate} disabled={busy} className="flex-1">
          {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Generate grid
        </Button>
        <Button variant="ghost" onClick={onDone}>Skip</Button>
      </div>
    </StepCard>
  );
}

function CarrierStep({
  tenantId, busy, setBusy, onDone,
}: { tenantId: string; busy: boolean; setBusy: (v: boolean) => void; onDone: () => void }) {
  const [name, setName] = useState("UPS");
  const [code, setCode] = useState("ups");
  const createCarrierFn = useServerFn(createCarrier);

  const create = async () => {
    if (!name.trim() || !code.trim()) return toast.error("Name and code required");
    setBusy(true);
    try {
      await createCarrierFn({ data: { tenantId, name: name.trim(), code: code.trim().toLowerCase() } });
      toast.success("Carrier added");
      onDone();
    } catch (err) { toast.error((err as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <StepCard title="Add a Shipping Carrier">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1"><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></div>
        <div className="space-y-1"><Label>Code</Label><Input value={code} onChange={(e) => setCode(e.target.value)} /></div>
      </div>
      <div className="flex gap-2">
        <Button onClick={create} disabled={busy} className="flex-1">
          {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Add carrier
        </Button>
        <Button variant="ghost" onClick={onDone}>Skip</Button>
      </div>
    </StepCard>
  );
}

function CustomerStep({
  tenantId, busy, setBusy, onCreated,
}: { tenantId: string; busy: boolean; setBusy: (v: boolean) => void; onCreated: (id: string) => void }) {
  const [name, setName] = useState("");
  const createCustomerFn = useServerFn(createCustomer);

  const create = async () => {
    if (!name.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      const res = await createCustomerFn({ data: { tenantId, name: name.trim(), contactInfo: {} } });
      toast.success("Customer created");
      onCreated(res.id);
    } catch (err) { toast.error((err as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <StepCard title="Create the First Customer (brand)">
      <div className="grid grid-cols-1 gap-3">
        <div className="space-y-1"><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Brand" /></div>
      </div>
      <Button onClick={create} disabled={busy} className="w-full">
        {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create customer
      </Button>
    </StepCard>
  );
}

function SkuStep({
  tenantId, customerId, busy, setBusy, onDone,
}: { tenantId: string; customerId: string; busy: boolean; setBusy: (v: boolean) => void; onDone: () => void }) {
  const [code, setCode] = useState("SKU-001");
  const [name, setName] = useState("Sample product");
  const createSkuFn = useServerFn(createSku);

  const create = async () => {
    if (!code.trim() || !name.trim()) return toast.error("Code and name required");
    setBusy(true);
    try {
      await createSkuFn({ data: { tenantId, customerId, skuCode: code.trim(), name: name.trim() } });
      toast.success("SKU created");
      onDone();
    } catch (err) { toast.error((err as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <StepCard title="Create the First SKU">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1"><Label>SKU code</Label><Input value={code} onChange={(e) => setCode(e.target.value)} /></div>
        <div className="space-y-1"><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></div>
      </div>
      <div className="flex gap-2">
        <Button onClick={create} disabled={busy} className="flex-1">
          {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create SKU
        </Button>
        <Button variant="ghost" onClick={onDone}>Skip</Button>
      </div>
    </StepCard>
  );
}